<template>
	<AppLayout
		:title="assembly.title"
		:subtitle="election && canVote ? election.title : ''"
		:return-url="route('association.assemblies.index')"
		:return-label="trans('menu.association.assemblies')"
	>
		<template #actions>
			<a :href="assembly.meeting.link" target="_blank">
				<SecondaryButton :color="Color.Blue">
					<i class="fa-solid fa-video-camera mr-2"/>
					{{ trans('assemblies.meeting') }}
				</SecondaryButton>
			</a>
			<PrimaryButton v-if="canVote && election" :color="Color.Green" @click.stop="vote">
				<i class="fa-solid fa-vote-yea mr-2"/>
				{{ trans('roles.actions.vote') }}
			</PrimaryButton>
		</template>
		<template v-if="canVote && election">
			<Card v-for="electionCandidate in election.candidates" :key="`election-${election.id}-candidate-${electionCandidate.id}`" class="hover:bg-gray-100 dark:hover:bg-slate-900 cursor-pointer" @click.stop="toggleVote(electionCandidate)">
				<div class="flex items-center gap-3">
					<i v-if="candidateIsSelected(electionCandidate)" class="fa-solid fa-circle-check text-lg text-green-600 dark:text-green-400"/>
					<i v-else class="fa-regular fa-circle text-lg"/>
					<CardTitle>{{ electionCandidate.candidate.title }}</CardTitle>
				</div>
			</Card>
		</template>
		<p v-else class="text-center text-3xl font-light text-gray-400 dark:text-gray-600">{{ trans('assemblies.elections.wait') }}</p>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {usePage} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import {useEchoModel} from '@laravel/echo-vue'
import Card from '@/Components/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import CardTitle from '@/Components/Cards/CardTitle.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/helpers'
import {type Assembly} from '@/Types/Association/Assemblies/Assembly'
import SecondaryButton from '@/Components/Buttons/SecondaryButton.vue'
import {type ElectionCandidate} from '@/Types/Elections/ElectionCandidate'
import {type AssemblyElection} from '@/Types/Elections/Assemblies/AssemblyElection'
import {ref, type Ref, defineProps, onMounted, type ComputedRef, computed} from 'vue'

const props = defineProps<{
	canVote:boolean,
	assembly:Assembly,
	selected:Array<number>,
	election:AssemblyElection|null,
}>()

const page = usePage()

const working:Ref<boolean> = ref(false)
const canVote:Ref<boolean> = ref(props.canVote)
const assembly:Ref<Assembly> = ref(props.assembly)
const election:Ref<AssemblyElection|null> = ref(props.election)
const selected:Ref<Array<number>> = ref(props.selected ?? [])

const userId:ComputedRef<number> = computed(():number => page.props.userId as number)

function toggleVote(candidate:ElectionCandidate<any>):void{
	if(candidateIsSelected(candidate)){
		const index = selected.value.findIndex((s:number) => s === candidate.id)
		if(index > -1){
			selected.value.splice(index, 1)
		}
	} else {
		if(election.value?.limit === 1 && selected.value.length === 1){
			selected.value.splice(0, 1)
		}
		selected.value.push(candidate.id)
	}
}

function candidateIsSelected(candidate:ElectionCandidate<any>):boolean{
	return selected.value.includes(candidate.id)
}

function vote():void{
	if(!working.value && election.value && confirm(trans('assemblies.elections.confirm_vote'))){
		working.value = true
		showInfo(trans('assemblies.elections.voting'))
		axios.post(route('association.assemblies.update', assembly.value.id), {
			selected: selected.value,
		}).then(() => {
			showSuccess(trans('assemblies.elections.voted'))
			election.value = undefined
			canVote.value = false
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

onMounted(():void => {
	const userEchoModel = useEchoModel('users', userId.value)
	userEchoModel.channel().listen('CollegeZoneRepresentativeElectionStarted', (event:{election:AssemblyElection}) => {
		election.value = event.election
		canVote.value = true
	})
	const assemblyEchoModel = useEchoModel('assemblies', assembly.value.id)
	assemblyEchoModel.channel().listen('LegalRankElectionStarted', (event:{election:AssemblyElection}) => {
		election.value = event.election
		canVote.value = true
	})
	assemblyEchoModel.channel().listen('EventHostElectionStarted', (event:{election:AssemblyElection}) => {
		election.value = event.election
		canVote.value = true
	})
	assemblyEchoModel.channel().listen('AssemblyElectionEnded', () => {
		election.value = undefined
		canVote.value = false
	})
})
</script>